import React from 'react';
import dayjs from 'dayjs';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import api from '../../../api';
import { PanelContainer } from '../../../components/panel';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { MedicalResidentSettingForm } from './medicalResidentSettingForm';
import { color } from '../../../styles';
import { CARE_LEVEL, DEFAULT_RESIDENT_FORM_VALUE, HOURS, INDEPENDENCE_LEVELS, LOCAL_HOURS } from '../../../constants';
import { ResidentSettingForm } from './residentSettingForm';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { selfDataSelector } from '../../../redux/config/selfData';
import { isDemoContractSelector, showLifeContentSelector } from '../../../redux/config/currentNursingHome';
import { ICreateResidentParams } from '../../../types/api/resident/createResident';
import { IGetResidentRes } from '../../../types/api/resident/getResident';
import { IGetGoodTreeRes } from '../../../types/api/good-tree/getGoodTree';
import { IGetNursingHomeRes } from '../../../types/api/nursingHome/getNursingHome';
import { IUpdateResidentParams } from '../../../types/api/resident/updateResident';
import { IGetDiaperRes } from '../../../types/api/care-settings/getDiaper';
import { IGetDfreeDevicesRes } from '../../../types/api/dfree-devices/getDfreeDevices';
import { convertTzToUtcHourString, convertUtcToTzHourString } from '../../../utils/dateutil';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';
import { Button } from '../../../components/mui/button';
import { OutlineButton } from '../../../components/outlineButton';
import { Loading } from '../../../components/mui/Loading';
import { useLoading } from '../../../contexts/LoadingContext';
import { Dialog } from '../../../components/mui/dialog';
import { IGetDfreeBDevicesRes } from '@/types/api/dfree-b-devices/getDfreeBDevices';
import { IResidentCareSettingParams } from '@/types/api/resident/residentCareSettings';
import { IGetCdcRes } from '@/types/api/cdc/cdc';
import { IGetParamountRes } from '@/types/api/paramount/getParamount';

dayjs.extend(isSameOrAfter);

interface Props {
  isEdit?: boolean;
  residentId?: number;
}

const PanelButtonContainer = css`
  display: flex;
  gap: 0 16px;
`;

const LowerButtonContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteButton = styled(Button)`
  color: ${color.red};
`;

const DialogText = css`
  margin-bottom: 16px;
  font-size: 14px;
`;

const DialogAlertText = css`
  color: ${color.red};
  font-size: 14px;
  font-weight: bold;
`;

const DialogContainer = css`
  margin: 8px;
  text-align: center;
`;

const EmptyContainer = css`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 14px;
  padding: 24px;
  background-color: ${color.bg_light_green};
  display: flex;
`;

export const ResidentSetting: React.FC<Props> = ({ isEdit = false, residentId }) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const location = useLocation();
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: DEFAULT_RESIDENT_FORM_VALUE,
    mode: 'onBlur',
  });
  const {
    name,
    lastName,
    sex,
    careSettings,
    bigBladderNotification,
    urinationNotification,
    gotUpNotification,
    bigBladderSnoozeTerm,
    urinationSnoozeTerm,
    gotUpSnoozeTerm,
    bigBladderSnoozePeriod,
    urinationSnoozePeriod,
    gotUpSnoozePeriod,
    goodTreeResidentId,
    careDataConnectResidentId,
    paramountResidentId,
  } = useWatch({
    control,
  });
  const { selfUnits } = useSelector(selfDataSelector);
  const showLifeContent = useSelector(showLifeContentSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const isDemoContract = useSelector(isDemoContractSelector);
  const [selectData, setSelectData] = React.useState<{
    pants: IGetDiaperRes[];
    pads: IGetDiaperRes[];
    devices: IGetDfreeDevicesRes[];
    goodTreeList: IGetGoodTreeRes[];
    cdcList: IGetCdcRes[];
    bDevices: IGetDfreeBDevicesRes[];
    paramountList: IGetParamountRes;
  }>();
  const [deviceId, setDeviceId] = React.useState<string>('');
  const [bDeviceId, setBDeviceId] = React.useState<string>('');
  const [careSchedule, setCareSchedule] = React.useState<string[]>(['', '', '']);
  const [isOpenDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [isOpenAttachedDeviceDialog, setOpenAttachedDeviceDialog] = React.useState(false);
  const [isOpenAttachedBDeviceDialog, setOpenAttachedBDeviceDialog] = React.useState(false);
  const [isOpenNameChangeDialog, setOpenNameChangeDialog] = React.useState(false);
  const [pendingSubmitData, setPendingSubmitData] = React.useState<ICreateResidentParams | IUpdateResidentParams | null>(
    null,
  );
  const [resident, setResident] = React.useState<IGetResidentRes>();
  const [nursingHome, setNursingHome] = React.useState<IGetNursingHomeRes>();
  const [onSend, setOnSend] = React.useState<boolean>(false);
  const { t, i18n, serviceT } = useServiceTranslation();
  const isNavigatedNotificationSettings = location.hash === '#notification-settings';

  const fetchResident = React.useCallback(async () => {
    if (!isEdit) return;
    try {
      showLoading();
      let resident = await api.get('/residents/:residentId', { params: { residentId } });
      const careSchedule = await api.get('/residents/:residentId/care-schedule', { params: { residentId } });
      /* 排泄ケア時刻の初期値設定 */
      /* eslint-disable-next-line no-extra-boolean-cast */
      const localTimings = careSchedule.careTimings
        .map((timing) => {
          const hours = +timing.slice(0, 2);
          const minutes = +timing.slice(3, 5);
          return dayjs.utc().hour(hours).minute(minutes).second(0).local().format('HH:mm:ss');
        })
        .sort((a, b) => (a > b ? 1 : -1));
      const paddedLocalTimings =
        localTimings.length < 3 ? localTimings.concat(Array(3 - localTimings.length).fill('')) : localTimings;
      setCareSchedule(paddedLocalTimings);

      // ローカル時間だがUTC時間であるZがついている
      if (resident) {
        resident = {
          ...resident,
          beginGotUpCheckTerm: resident.beginGotUpCheckTerm
            ? convertUtcToTzHourString(resident.beginGotUpCheckTerm)
            : '19:00:00Z',
          endGotUpCheckTerm: resident.endGotUpCheckTerm
            ? convertUtcToTzHourString(resident.endGotUpCheckTerm)
            : '07:00:00Z',
          // スヌーズ間隔がnullの場合はなしを補完している
          bigBladderSnoozeTerm: resident.bigBladderSnoozeTerm ? resident.bigBladderSnoozeTerm : 0,
          urinationSnoozeTerm: resident.urinationSnoozeTerm ? resident.urinationSnoozeTerm : 0,
          gotUpSnoozeTerm: resident.gotUpSnoozeTerm ? resident.gotUpSnoozeTerm : 0,
          // スヌーズ終了がnullの場合はデフォルト値(1時間)を補完している
          bigBladderSnoozePeriod: resident.bigBladderSnoozePeriod ? resident.bigBladderSnoozePeriod : 60,
          urinationSnoozePeriod: resident.urinationSnoozePeriod ? resident.urinationSnoozePeriod : 60,
          gotUpSnoozePeriod: resident.gotUpSnoozePeriod ? resident.gotUpSnoozePeriod : 60,
          careSettings: {
            ...resident.careSettings,
            beginDiaperMiddleHour: resident.careSettings.beginDiaperMiddleHour
              ? convertUtcToTzHourString(resident.careSettings.beginDiaperMiddleHour)
              : hoursWithUnSelected[0].key,
            endDiaperMiddleHour: resident.careSettings.endDiaperMiddleHour
              ? convertUtcToTzHourString(resident.careSettings.endDiaperMiddleHour)
              : hoursWithUnSelected[0].key,
            beginDiaperNightHour: resident.careSettings.beginDiaperNightHour
              ? convertUtcToTzHourString(resident.careSettings.beginDiaperNightHour)
              : hoursWithUnSelected[0].key,
            endDiaperNightHour: resident.careSettings.endDiaperNightHour
              ? convertUtcToTzHourString(resident.careSettings.endDiaperNightHour)
              : hoursWithUnSelected[0].key,
          },
          gasDuration: resident.gasDuration ?? 3,
          gasStrength: resident.gasStrength ?? 3,
        };
      }

      setDeviceId(String(resident.dfreeDeviceId ?? ''));
      setBDeviceId(String(resident.dfreeBDeviceId ?? ''));
      setResident(resident);
      reset({ ...resident });
      hideLoading();
    } catch {
      history.push('/');
    }
  }, []);

  const fetchNursingHome = React.useCallback(async () => {
    try {
      showLoading();
      const nursingHome = await api.get('/nursing-homes/:nursingHomeId', { params: { nursingHomeId } });
      setNursingHome(nursingHome);
      hideLoading();
      return nursingHome;
    } catch {
      history.push('/');
    }
  }, [nursingHomeId]);

  // フォームのセレクトに必要なデータを取得
  const fetchSelectData = React.useCallback(
    async (goodTree?: boolean, cdc?: boolean, paramount?: boolean) => {
      const diapersResponse = await api.get('/nursing-homes/:nursingHomeId/diapers', { params: { nursingHomeId } });
      const pants = diapersResponse.filter((diapersResponse) => diapersResponse.diaperShape === 'outer');
      const pads = diapersResponse.filter((diapersResponse) => diapersResponse.diaperShape === 'inner');
      const devices = await api.get('/nursing-homes/:nursingHomeId/dfree-devices', { params: { nursingHomeId } });

      let goodTreeList: any[] = [];
      if (goodTree) {
        try {
          goodTreeList = await api.get('/nursing-homes/:nursingHomeId/residents/external/good-tree', {
            params: { nursingHomeId },
          });
        } catch (error) {
          goodTreeList = [];
        }
      }

      let cdcList: any[] = [];
      if (isEdit && cdc) {
        try {
          cdcList = await api.get('/external/cdc/nursing-homes/:nursingHomeId/residents', {
            params: { nursingHomeId },
          });
        } catch (error) {
          cdcList = [];
        }
      }
      let paramountList: any[] = [];
      if (isEdit && paramount) {
        try {
          paramountList = await api.get('/external/paramount/nursing-homes/:nursingHomeId/residents', {
            params: { nursingHomeId },
          });
        } catch (error) {
          paramountList = [];
        }
      }
      const bDevices = await api.get('/nursing-homes/:nursingHomeId/dfree-b-devices', { params: { nursingHomeId } });
      setSelectData({ pants, pads, devices, goodTreeList, cdcList, bDevices, paramountList });
    },
    [nursingHomeId],
  );

  const handleChangeDeviceId = React.useCallback((id: string) => {
    setDeviceId(id);
  }, []);

  const handleChangeBDeviceId = React.useCallback((id: string) => {
    setBDeviceId(id);
  }, []);

  const handleChangeCareSchedule = React.useCallback(
    (editType: 'add' | 'change' | 'delete', schedule?: string, index?: number) => {
      setCareSchedule((prev) => {
        if (editType === 'add') return [...prev, localHoursWithUnSelected[0].key];

        if (editType === 'change' && index != null && schedule != null) {
          const newSchedule = [...prev];
          newSchedule[index] = schedule;
          return newSchedule;
        }

        if (editType === 'delete' && index != null) {
          if (prev.length >= 4) {
            return prev.filter((_, i) => i !== index);
          } else if (prev.length <= 3) {
            const newSchedule = [...prev];
            newSchedule[index] = '';
            return newSchedule;
          }
        }

        return prev;
      });
    },
    [careSchedule],
  );

  // Selectコンポーネントのvalueが0のものはnullとして扱う
  const formatSnooze = (value: number | null) => {
    if (value === 0) return null;
    else return value;
  };

  const formatDiaperHour = (value: string | null, isEmpty: boolean) => {
    if (value === null || isEmpty) return null;
    else return convertTzToUtcHourString(value);
  };

  // セレクトボックスで「未選択」の場合はnullを返す
  const formatSelectedValue = <T extends number | string | null>(value: T, isEmpty: boolean): T => {
    if (value === null || isEmpty) return null as T;
    return value;
  };

  const preSubmit = async (data: ICreateResidentParams | IUpdateResidentParams) => {
    /* eslint-disable-next-line no-extra-boolean-cast */
    const device = !!deviceId
      ? selectData?.devices.find((device) => device.id === +deviceId)
      : selectData?.devices.find((device) => device.id === resident?.dfreeDeviceId);
    const deviceLinkedResidentId = device?.residentId;

    if (deviceLinkedResidentId && deviceLinkedResidentId != residentId) {
      handleOpenAttachedDeviceDialog();
    } else {
      preSubmitSecond(data);
    }
  };

  const preSubmitSecond = async (data: ICreateResidentParams | IUpdateResidentParams) => {
    // eslint-disable-next-line no-extra-boolean-cast
    const bDevice = !!bDeviceId
      ? selectData?.bDevices.find((device) => device.id === +bDeviceId)
      : selectData?.bDevices.find((device) => device.id === resident?.dfreeBDeviceId);
    const bDeviceLinkedResidentId = bDevice?.residentId;

    handleCloseAttachedDeviceDialog();

    if (bDeviceLinkedResidentId && bDeviceLinkedResidentId != residentId) {
      handleOpenAttachedBDeviceDialog();
    } else {
      submit(data);
    }
  };

  const handlePreSubmit = (data: ICreateResidentParams | IUpdateResidentParams) => {
    const isNameChanged =
      isEdit &&
      !showMedicalContent &&
      !!resident &&
      (data.lastName !== resident.lastName || data.name !== resident.name);

    if (isNameChanged) {
      setPendingSubmitData(data);
      setOpenNameChangeDialog(true);
      return;
    }

    preSubmit(data);
  };

  // 片側のみ選択かどうか（両方未入力 or 両方入力以外）を判定
  const isOnlyOneSelectedMiddle = (value: string | null, isBegin: boolean) => {
    const beginSelected = !!(isBegin ? value : careSettings?.beginDiaperMiddleHour);
    const endSelected = !!(isBegin ? careSettings?.endDiaperMiddleHour : value);
    return beginSelected !== endSelected;
  };

  const isOnlyOneSelectedNight = (value: string | null, isBegin: boolean) => {
    const beginSelected = !!(isBegin ? value : careSettings?.beginDiaperNightHour);
    const endSelected = !!(isBegin ? careSettings?.endDiaperNightHour : value);
    return beginSelected !== endSelected;
  };

  const submit = async (data: ICreateResidentParams | IUpdateResidentParams) => {
    setOnSend(true);

    const unit = selfUnits.find((unit) => unit.id === data.nursingHomeUnitId);
    if (!unit) {
      showSnackbar(
        t('unit', 'ユニット') + (i18n.language.startsWith('ja') ? '' : ' ') + t('isRequired', 'は必須入力です。'),
        'error',
      );
      setOnSend(false);
      return;
    }

    const hourKeys: Array<keyof IUpdateResidentParams & keyof ICreateResidentParams> = [
      'beginGotUpCheckTerm',
      'endGotUpCheckTerm',
    ];

    const snoozeKeys: Array<keyof IUpdateResidentParams & keyof ICreateResidentParams> = [
      'bigBladderSnoozeTerm',
      'urinationSnoozeTerm',
      'gotUpSnoozeTerm',
      'bigBladderSnoozePeriod',
      'urinationSnoozePeriod',
      'gotUpSnoozePeriod',
    ];

    const diaperHourKeys: Array<keyof IResidentCareSettingParams> = [
      'beginDiaperMiddleHour',
      'endDiaperMiddleHour',
      'beginDiaperNightHour',
      'endDiaperNightHour',
    ];

    const selectedValueKeys: Array<keyof IResidentCareSettingParams> = [
      'independenceLevel',
      'nursingHomeDiaperIdNoon',
      'nursingHomePadIdNoonFirst',
      'nursingHomePadIdNoonSecond',
      'nursingHomeDiaperIdMiddle',
      'nursingHomePadIdMiddleFirst',
      'nursingHomePadIdMiddleSecond',
      'nursingHomeDiaperIdNight',
      'nursingHomePadIdNightFirst',
      'nursingHomePadIdNightSecond',
      'careLevel',
    ];

    const params: ICreateResidentParams | IUpdateResidentParams = {
      ...data,
      ...Object.fromEntries(hourKeys.map((key) => [key, convertTzToUtcHourString(data[key] as string)])),
      ...Object.fromEntries(snoozeKeys.map((key) => [key, formatSnooze(data[key] as number | null)])),
      careSettings: {
        ...data.careSettings,
        ...Object.fromEntries(
          diaperHourKeys.map((key) => [
            key,
            formatDiaperHour(data.careSettings[key] as string | null, isEmptyFormData[key]),
          ]),
        ),
        ...Object.fromEntries(
          selectedValueKeys.map((key) => [
            key,
            formatSelectedValue(data.careSettings[key] as string | null, isEmptyFormData[key]),
          ]),
        ),
      },
      nursingHomeUnitName: unit.name,
      /* eslint-disable-next-line no-extra-boolean-cast */
      dfreeDeviceId: !!deviceId ? +deviceId : null,
      // eslint-disable-next-line no-extra-boolean-cast
      dfreeBDeviceId: !!bDeviceId ? +bDeviceId : null,
      goodTreeResidentId: isEmptyFormData['goodTreeResidentId'] ? null : data.goodTreeResidentId,
      careDataConnectResidentId: isEmptyFormData['careDataConnectResidentId'] ? null : data.careDataConnectResidentId,
      paramountResidentId: isEmptyFormData['paramountResidentId'] ? null : data.paramountResidentId,
    };

    try {
      let currentResidentId = null;
      if (isEdit) {
        await api.put('/residents/:residentId', { residentId, ...params });
        currentResidentId = residentId;
      } else {
        const res = await api.post('/nursing-homes/:nursingHomeId/residents', { ...params });
        currentResidentId = res.id;
      }
      // 未入力のフォームを除外し、排泄ケア時刻をUTC時間に変換
      if (careSchedule.length > 0) {
        const utcCareSchedule = careSchedule
          .filter((v) => v !== localHoursWithUnSelected[0].key)
          .map((timing) => {
            const hours = +timing.slice(0, 2);
            const minutes = +timing.slice(3, 5);
            return dayjs().hour(hours).minute(minutes).second(0).utc().format('HH:mm:ss[Z]');
          });
        await api.put('/residents/:residentId/care-schedule', {
          residentId: currentResidentId,
          careTimings: utcCareSchedule,
        });
      }
      if (careSchedule.length == 0) {
        await api.put('/residents/:residentId/care-schedule', {
          residentId: currentResidentId,
          careTimings: [],
        });
      }
      setOnSend(false);
      showSnackbar(
        isEdit ? serviceT('residentHasBeenUpdated') : serviceT('residentHasBeenAdded'),
      );
      history.push('/');
    } catch (e) {
      setOnSend(false);
      showSnackbar(t('somethingWentWrong', 'エラーが発生しました'), 'error');
    }
  };

  const isAfternoonEndBeforeNightStart = (value: string | null) => {
    if (
      !value ||
      value === '-' ||
      !careSettings ||
      !careSettings.endDiaperMiddleHour ||
      !careSettings.beginDiaperNightHour
    )
      return false;
    return dayjs()
      .hour(+value.slice(0, 2))
      .isAfter(dayjs().hour(+careSettings.beginDiaperNightHour.slice(0, 2)));
  };

  const deleteResident = React.useCallback(async () => {
    await api.delete('/residents/:residentId', { params: { residentId } });
    showSnackbar(serviceT('residentHasBeenDeleted'));
    history.push('/');
  }, []);

  const handleNavigateLifeSettingsPage = React.useCallback(() => {
    history.push('./life/edit');
  }, []);

  const handleOpenDeleteDialog = React.useCallback(() => {
    setOpenDeleteDialog(true);
  }, []);

  const handleCloseDeleteDialog = React.useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleOpenAttachedDeviceDialog = React.useCallback(() => {
    setOpenAttachedDeviceDialog(true);
  }, []);

  const handleCloseAttachedDeviceDialog = React.useCallback(() => {
    setOpenAttachedDeviceDialog(false);
  }, []);

  const handleOpenAttachedBDeviceDialog = React.useCallback(() => {
    setOpenAttachedBDeviceDialog(true);
  }, []);

  const handleCloseAttachedBDeviceDialog = React.useCallback(() => {
    setOpenAttachedBDeviceDialog(false);
  }, []);

  const handleCloseNameChangeDialog = React.useCallback(() => {
    setOpenNameChangeDialog(false);
    setPendingSubmitData(null);
  }, []);

  const handleContinueNameChangeDialog = React.useCallback(() => {
    if (!pendingSubmitData) {
      setOpenNameChangeDialog(false);
      return;
    }
    const data = pendingSubmitData;
    setOpenNameChangeDialog(false);
    setPendingSubmitData(null);
    preSubmit(data);
  }, [pendingSubmitData, preSubmit]);

  const handleCreateNewResident = React.useCallback(() => {
    setOpenNameChangeDialog(false);
    setPendingSubmitData(null);
    history.push(`/nursinghome/${nursingHomeId}/residents/new`);
  }, [history, nursingHomeId]);

  const unSelectedOption = { key: '-', value: t('notSelected', t('notSelected', '未選択')) };
  const unSelectedEmptyOption = { key: '', value: t('notSelected', t('notSelected', '未選択')) };

  const hoursWithUnSelected = [unSelectedEmptyOption, ...HOURS];
  const localHoursWithUnSelected = [unSelectedEmptyOption, ...LOCAL_HOURS];

  const memoFormData = React.useMemo(() => {
    if (!selectData) return;
    const pants = [
      unSelectedOption,
      ...selectData.pants.map((pant) => {
        return { key: pant.id, value: pant.diaperName };
      }),
    ];
    const pads = [
      unSelectedOption,
      ...selectData.pads.map((pad) => {
        return { key: pad.id, value: pad.diaperName };
      }),
    ];
    const devices = selectData.devices;
    const goodTreeList = [
      unSelectedOption,
      ...selectData.goodTreeList.map((goodTree) => {
        return { key: goodTree.id, value: `${goodTree.sei} ${goodTree.mei}` };
      }),
    ];
    const cdcList = [
      unSelectedOption,
      ...selectData.cdcList.map((cdc) => {
        return { key: cdc.cdcCareReceiverId, value: cdc.cdcCareReceiverName };
      }),
    ];
    const paramountList = [
      unSelectedOption,
      ...selectData.paramountList.map((p: any) => {
        return { key: p.userId, value: `${p.userNameSei} ${p.userNameMei}` };
      }),
    ];
    const bDevices = selectData.bDevices;

    const careLevel = [
      unSelectedOption,
      ...CARE_LEVEL.map((v) => {
        return { key: v.key, value: v.value };
      }),
    ];
    return { pants, pads, devices, goodTreeList, cdcList, paramountList, bDevices, careLevel };
  }, [selectData, CARE_LEVEL]);

  // 選択状態が「未選択」か判定
  const isEmptyFormData: Record<string, boolean> = React.useMemo(() => {
    return {
      independenceLevel: careSettings?.independenceLevel === INDEPENDENCE_LEVELS[0].key,
      nursingHomeDiaperIdNoon: careSettings?.nursingHomeDiaperIdNoon === memoFormData?.pants[0].key,
      nursingHomePadIdNoonFirst: careSettings?.nursingHomePadIdNoonFirst === memoFormData?.pads[0].key,
      nursingHomePadIdNoonSecond: careSettings?.nursingHomePadIdNoonSecond === memoFormData?.pads[0].key,
      beginDiaperMiddleHour:
        careSettings?.beginDiaperMiddleHour === null ||
        careSettings?.beginDiaperMiddleHour === hoursWithUnSelected[0].key,
      endDiaperMiddleHour:
        careSettings?.endDiaperMiddleHour === null || careSettings?.endDiaperMiddleHour === hoursWithUnSelected[0].key,
      nursingHomeDiaperIdMiddle: careSettings?.nursingHomeDiaperIdMiddle === memoFormData?.pants[0].key,
      nursingHomePadIdMiddleFirst: careSettings?.nursingHomePadIdMiddleFirst === memoFormData?.pads[0].key,
      nursingHomePadIdMiddleSecond: careSettings?.nursingHomePadIdMiddleSecond === memoFormData?.pads[0].key,
      beginDiaperNightHour:
        careSettings?.beginDiaperNightHour === null ||
        careSettings?.beginDiaperNightHour === hoursWithUnSelected[0].key,
      endDiaperNightHour:
        careSettings?.endDiaperNightHour === null || careSettings?.endDiaperNightHour === hoursWithUnSelected[0].key,
      nursingHomeDiaperIdNight: careSettings?.nursingHomeDiaperIdNight === memoFormData?.pants[0].key,
      nursingHomePadIdNightFirst: careSettings?.nursingHomePadIdNightFirst === memoFormData?.pads[0].key,
      nursingHomePadIdNightSecond: careSettings?.nursingHomePadIdNightSecond === memoFormData?.pads[0].key,
      careLevel: careSettings?.careLevel === memoFormData?.careLevel[0].key,
      goodTreeResidentId: goodTreeResidentId === null || goodTreeResidentId === memoFormData?.goodTreeList[0].key,
      careDataConnectResidentId:
        careDataConnectResidentId === null || careDataConnectResidentId === memoFormData?.cdcList[0].key,
      paramountResidentId: paramountResidentId === null || paramountResidentId === memoFormData?.paramountList[0].key,
    };
  }, [
    careSettings?.independenceLevel,
    careSettings?.nursingHomeDiaperIdNoon,
    careSettings?.nursingHomePadIdNoonFirst,
    careSettings?.nursingHomePadIdNoonSecond,
    careSettings?.beginDiaperMiddleHour,
    careSettings?.endDiaperMiddleHour,
    careSettings?.nursingHomeDiaperIdMiddle,
    careSettings?.nursingHomePadIdMiddleFirst,
    careSettings?.nursingHomePadIdMiddleSecond,
    careSettings?.beginDiaperNightHour,
    careSettings?.endDiaperNightHour,
    careSettings?.nursingHomeDiaperIdNight,
    careSettings?.nursingHomePadIdNightFirst,
    careSettings?.nursingHomePadIdNightSecond,
    careSettings?.careLevel,
    goodTreeResidentId,
    careDataConnectResidentId,
    memoFormData,
    INDEPENDENCE_LEVELS,
    hoursWithUnSelected,
  ]);

  const handleChangeDetakamoLogic = (value: string) => {
    setValue('detakamoLogic', value as 'less' | 'often');
  };

  const handleChangeSnoozeTerm = (type: string, value: string | number) => {
    if (type === 'bigBladder' && bigBladderSnoozePeriod && +value > +bigBladderSnoozePeriod) {
      setValue('bigBladderSnoozePeriod', +value);
    }
    if (type === 'urination' && urinationSnoozePeriod && +value > +urinationSnoozePeriod) {
      setValue('urinationSnoozePeriod', +value);
    }
    if (type === 'gotUp' && gotUpSnoozePeriod && +value > +gotUpSnoozePeriod) {
      setValue('gotUpSnoozePeriod', +value);
    }
  };

  const handleChangeSnoozePeriod = (type: string, value: string | number) => {
    if (type === 'bigBladder' && bigBladderSnoozeTerm && +value < +bigBladderSnoozeTerm) {
      setValue('bigBladderSnoozeTerm', +value);
    }
    if (type === 'urination' && urinationSnoozeTerm && +value < +urinationSnoozeTerm) {
      setValue('urinationSnoozeTerm', +value);
    }
    if (type === 'gotUp' && gotUpSnoozeTerm && +value < +gotUpSnoozeTerm) {
      setValue('gotUpSnoozeTerm', +value);
    }
  };

  const handleAddUnitButton = () => {
    history.push(`/nursinghome/${nursingHomeId}/settings/unit`);
  };

  const dfreeDeviceName = React.useMemo(() => {
    const deviceName = selectData?.devices.find((device) => device.id === +deviceId);
    return i18n.language.startsWith('ja')
      ? `${deviceName?.lastName}${deviceName?.firstName}`
      : `${deviceName?.firstName} ${deviceName?.lastName}`;
  }, [selectData, deviceId, i18n.language]);

  const benDeviceName = React.useMemo(() => {
    const deviceName = selectData?.bDevices.find((device) => device.id === +bDeviceId);
    return i18n.language.startsWith('ja')
      ? `${deviceName?.lastName}${deviceName?.firstName}`
      : `${deviceName?.firstName} ${deviceName?.lastName}`;
  }, [selectData, bDeviceId, i18n.language]);

  const residentName = React.useMemo(() => {
    return i18n.language.startsWith('ja') ? `${lastName || ''}${name || ''}` : `${name || ''} ${lastName || ''}`;
  }, [resident, lastName, name, i18n.language]);

  React.useEffect(() => {
    if (!nursingHomeId) return;

    const fetchData = async () => {
      if (isEdit) {
        await fetchResident();
      }
      const ret = await fetchNursingHome();
      await fetchSelectData(ret?.goodTree, ret?.cdc, ret?.inUseParamount);
    };

    fetchData();
    reset({ ...DEFAULT_RESIDENT_FORM_VALUE, nursingHomeId: +nursingHomeId });
  }, [nursingHomeId]);

  // URLで遷移してきた場合にハッシュターゲットにスクロールする処理
  React.useEffect(() => {
    if (!location.hash) return;

    const targetId = decodeURIComponent(location.hash.slice(1));
    let retryCount = 0;
    const maxRetries = 10;

    const scrollToHashTarget = () => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ block: 'start' });
        return;
      }

      if (retryCount >= maxRetries) return;
      retryCount += 1;
      window.setTimeout(scrollToHashTarget, 300);
    };

    scrollToHashTarget();
  }, [location.hash, memoFormData, nursingHome, showMedicalContent]);

  return (
    <>
      <PanelContainer
        title={isEdit ? serviceT('editResident') : serviceT('addResident')}
        fixable={true}
        rightElement={
          <div css={PanelButtonContainer}>
            <Button variant='contained' onClick={handleSubmit(handlePreSubmit)} type='submit' disabled={onSend}>
              {isEdit ? t('save', '保存') : serviceT('addResident')}
            </Button>
            {isEdit && (
              <>
                {showLifeContent && (
                  <OutlineButton onClick={handleNavigateLifeSettingsPage}>{'LIFE利用者設定'}</OutlineButton>
                )}
                <DeleteButton onClick={handleOpenDeleteDialog}>{serviceT('deleteResident')}</DeleteButton>
              </>
            )}
          </div>
        }
      >
        {selfUnits.length > 0 ? (
          <>
            {memoFormData && nursingHome ? (
              <form onSubmit={handleSubmit(handlePreSubmit)}>
                {showMedicalContent ? (
                  <MedicalResidentSettingForm
                    isEdit={isEdit}
                    isDemoContract={isDemoContract}
                    control={control}
                    units={selfUnits}
                    formSelectLists={memoFormData}
                    careSchedule={careSchedule}
                    deviceId={deviceId}
                    bDeviceId={bDeviceId}
                    isDisplayBigBladderOption={bigBladderNotification}
                    isDisplayUrinationOption={urinationNotification}
                    isDisplayGotUpOption={gotUpNotification}
                    nameError={!!errors.name}
                    lastNameError={!!errors.lastName}
                    sexError={!!errors.sex}
                    unitError={!!errors.nursingHomeUnitId}
                    endDiaperMiddleHourError={!!errors.careSettings?.endDiaperMiddleHour}
                    isAfternoonEndBeforeNightStart={isAfternoonEndBeforeNightStart}
                    isOnlyOneSelectedMiddle={isOnlyOneSelectedMiddle}
                    isOnlyOneSelectedNight={isOnlyOneSelectedNight}
                    onChangeCareSchedule={handleChangeCareSchedule}
                    onChangeDeviceId={handleChangeDeviceId}
                    onChangeBDeviceId={handleChangeBDeviceId}
                    onChangeSnoozeTerm={handleChangeSnoozeTerm}
                    onChangeSnoozePeriod={handleChangeSnoozePeriod}
                    onChangeDetakamoLogic={handleChangeDetakamoLogic}
                    nursingHome={nursingHome}
                    isEmptyFormData={isEmptyFormData}
                    hoursWithUnSelected={hoursWithUnSelected}
                    localHoursWithUnSelected={localHoursWithUnSelected}
                  />
                ) : (
                  <ResidentSettingForm
                    isEdit={isEdit}
                    isDemoContract={isDemoContract}
                    control={control}
                    units={selfUnits}
                    formSelectLists={memoFormData}
                    careSchedule={careSchedule}
                    deviceId={deviceId}
                    bDeviceId={bDeviceId}
                    isDisplayBigBladderOption={bigBladderNotification}
                    isDisplayUrinationOption={urinationNotification}
                    isDisplayGotUpOption={gotUpNotification}
                    nameError={!!errors.name}
                    lastNameError={!!errors.lastName}
                    unitError={!!errors.nursingHomeUnitId}
                    endDiaperMiddleHourError={!!errors.careSettings?.endDiaperMiddleHour}
                    isAfternoonEndBeforeNightStart={isAfternoonEndBeforeNightStart}
                    isOnlyOneSelectedMiddle={isOnlyOneSelectedMiddle}
                    isOnlyOneSelectedNight={isOnlyOneSelectedNight}
                    onChangeCareSchedule={handleChangeCareSchedule}
                    onChangeDeviceId={handleChangeDeviceId}
                    onChangeBDeviceId={handleChangeBDeviceId}
                    onChangeSnoozeTerm={handleChangeSnoozeTerm}
                    onChangeSnoozePeriod={handleChangeSnoozePeriod}
                    onChangeDetakamoLogic={handleChangeDetakamoLogic}
                    nursingHome={nursingHome}
                    isEmptyFormData={isEmptyFormData}
                    hoursWithUnSelected={hoursWithUnSelected}
                    localHoursWithUnSelected={localHoursWithUnSelected}
                    isNavigatedNotificationSettings={isNavigatedNotificationSettings}
                  />
                )}
                <div css={LowerButtonContainer}>
                  <Button variant='contained' type='submit' disabled={onSend}>
                    {isEdit ? t('save', '保存') : serviceT('addResident')}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <Loading />
              </>
            )}
          </>
        ) : (
          <div css={EmptyContainer}>
            <span>
              {serviceT('whenRegisteringANewResident')}
              {t('AUnitIsRequiredInAdvance', '事前にユニットの登録が必要です。')}
            </span>
            <span>{t('PleaseRegisterTheUnitFromTheButtonBelow', '下記ボタンからユニットの登録をしてください。')}</span>
            <br />
            <Button variant='contained' type='submit' onClick={handleAddUnitButton}>
              {t('RegisterUnit', 'ユニット登録ページへ')}
            </Button>
          </div>
        )}
      </PanelContainer>
      {isOpenNameChangeDialog && (
        <Dialog
          isOpen
          title={t('confirmResidentNameChangeTitle', '姓・名の変更を行いますか？')}
          cancelText='OK'
          okText={t('createNewResident', '新規入居者作成')}
          onClose={handleCloseNameChangeDialog}
          onCancelClick={handleContinueNameChangeDialog}
          onOkClick={handleCreateNewResident}
        >
          <div css={DialogContainer}>
            <div css={DialogText}>
              {t('confirmResidentNameChangeMessage', '装着者を変更したい場合は、新規で入居者を追加してください')}
              <br />
              {t('confirmResidentNameChangeMessageSub', '（複数人のデータ混在を防ぐため）')}
            </div>
          </div>
        </Dialog>
      )}
      {isOpenDeleteDialog && resident && (
        <Dialog
          isOpen
          title={serviceT('confirmDeleteResidentTitle')}
          onClose={handleCloseDeleteDialog}
          onOkClick={deleteResident}
        >
          <div css={DialogContainer}>
            <div css={DialogText}>
              {serviceT('confirmDeleteResident1')}
              <br />
              {serviceT('confirmDeleteResident2')}
            </div>
            <div css={DialogAlertText}>
              {resident.lastName}
              {resident.name} {resident.nursingHomeUnitName && <> - {resident.nursingHomeUnitName}</>}
            </div>
          </div>
        </Dialog>
      )}
      {isOpenAttachedDeviceDialog && (
        <Dialog isOpen title='' onClose={handleCloseAttachedDeviceDialog} onOkClick={handleSubmit(preSubmitSecond)}>
          <div css={DialogContainer}>
            {i18n.language.startsWith('ja') ? (
              <>
                <div css={DialogText}>
                  {dfreeDeviceName}さんのDFreeを{residentName}さんに紐付け変更しますがよろしいですか？
                </div>
              </>
            ) : (
              <>
                <div css={DialogText}>
                  Are you sure you want to link {dfreeDeviceName}’s DFree to {residentName}?
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
      {isOpenAttachedBDeviceDialog && (
        <Dialog isOpen title='' onClose={handleCloseAttachedBDeviceDialog} onOkClick={handleSubmit(submit)}>
          <div css={DialogContainer}>
            {i18n.language.startsWith('ja') ? (
              <>
                <div css={DialogText}>
                  {benDeviceName}さんのDFree Benを{residentName}さんに紐付け変更しますがよろしいですか？
                </div>
              </>
            ) : (
              <>
                <div css={DialogText}>
                  Are you sure you want to link {benDeviceName}’s DFree Ben to {residentName}?
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
    </>
  );
};
